import { useEffect, useState } from "react";
import { useAutoRefresh } from "../lib/autoRefresh";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { PageHeader, eur, dt, Badge, Btn } from "../components/common";
import {
  Euro, TrendingUp, Users2, AlertTriangle, Server, LifeBuoy, ShoppingCart, Activity, Repeat,
  ArrowRight, RefreshCw, ClipboardCheck, Clock, FileSignature, Target, Phone, Mail, MessageSquare,
  FileClock, Wallet, Trophy, PhoneMissed, CalendarClock, Check,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../auth";
import { ConnectorCredentialsBanner } from "../components/ConnectorCredentialsBanner";
import PlatformFilter from "../components/PlatformFilter";

const GENERAL_CARDS = [
  { key: "mrr", label: "MRR", icon: Euro, fmt: eur, to: "/app/subscricoes" },
  { key: "arr", label: "ARR", icon: TrendingUp, fmt: eur, to: "/app/subscricoes" },
  { key: "arpu", label: "ARPU", icon: Users2, fmt: eur, to: "/app/subscricoes" },
  { key: "churn", label: "Churn", icon: Activity, fmt: (v) => `${v} %`, to: "/app/subscricoes" },
  { key: "subscricoes_ativas", label: "Subscricoes ativas", icon: Repeat, to: "/app/subscricoes" },
  { key: "faturas_vencidas", label: "Faturas vencidas", icon: AlertTriangle, to: "/app/faturacao" },
  { key: "tickets_abertos", label: "Tickets abertos", icon: LifeBuoy, to: "/app/suporte" },
  { key: "pipeline", label: "Pipeline", icon: ShoppingCart, to: "/app/encomendas" },
];

const ACCENTS = {
  red: "border-l-red-500 text-red-600 bg-red-50",
  amber: "border-l-amber-500 text-amber-600 bg-amber-50",
  blue: "border-l-blue text-blue bg-tint",
  green: "border-l-emerald-500 text-emerald-600 bg-emerald-50",
  navy: "border-l-navy text-navy bg-slate-50",
};

function StatCard({ icon: Icon, label, value, sub, accent = "navy", to, delay = 0, testid, urgent }) {
  const nav = useNavigate();
  const cls = ACCENTS[accent] || ACCENTS.navy;
  const [ico, txt, bg] = cls.split(" ");
  return (
    <motion.button
      type="button"
      onClick={() => to && nav(to)}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      whileHover={{ y: -4 }}
      data-testid={testid}
      className={`group text-left bg-white border border-tint border-l-4 ${ico} rounded-xl p-5 hover:shadow-lg transition-all cursor-pointer relative overflow-hidden`}
    >
      <div className="flex items-start justify-between">
        <span className="text-muted2 text-[11px] uppercase tracking-[0.14em] font-semibold leading-tight max-w-[70%]">{label}</span>
        <span className={`shrink-0 w-9 h-9 rounded-lg ${bg} ${txt} flex items-center justify-center`}><Icon size={18} /></span>
      </div>
      <div className={`mt-3 text-3xl font-bold tracking-tight ${urgent ? txt : "text-navy"}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted2">{sub}</div>}
      {to && <div className={`flex items-center gap-1 text-xs ${txt} mt-3 opacity-0 group-hover:opacity-100 transition-opacity`}>Ver detalhe <ArrowRight size={12} /></div>}
    </motion.button>
  );
}

function Section({ title, children, right }) {
  return (
    <div className="mt-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-navy text-lg font-semibold tracking-tight">{title}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}


// Sparkline SVG de barras (7 dias)
function Sparkline7({ data, accentColor = "#ef4444" }) {
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map((d) => d.count), 1);
  const W = 140, H = 36, barW = 14, gap = 6;
  const dias = ["S", "D", "S", "T", "Q", "S", "S"]; // seg-dom generico (usa apenas indice)
  // Nomes do dia abreviados a partir da data
  const dayName = (iso) => {
    try { return new Date(iso + "T12:00:00Z").toLocaleDateString("pt-PT", { weekday: "short" }).slice(0, 1).toUpperCase(); }
    catch { return ""; }
  };
  return (
    <svg width={W} height={H + 14} aria-hidden="true" data-testid="sparkline-7d">
      {data.map((d, i) => {
        const barH = maxVal > 0 ? Math.max(3, Math.round((d.count / maxVal) * H)) : 3;
        const x = i * (barW + gap);
        const isHoje = i === data.length - 1;
        const fill = d.count === 0 ? "#e2e8f0" : isHoje ? accentColor : "#fca5a5";
        return (
          <g key={i}>
            <rect x={x} y={H - barH} width={barW} height={barH} rx={3} fill={fill} />
            {d.count > 0 && (
              <text x={x + barW / 2} y={H - barH - 3} textAnchor="middle" fontSize="8" fill="#64748b">{d.count}</text>
            )}
            <text x={x + barW / 2} y={H + 12} textAnchor="middle" fontSize="8" fill={isHoje ? "#64748b" : "#94a3b8"}>{dayName(d.data)}</text>
          </g>
        );
      })}
    </svg>
  );
}

// Widget compacto de chamadas perdidas e reagendamentos pendentes
function MissedCallsWidget({ cp, nav, onTaskComplete }) {
  const temHoje = cp.hoje > 0;
  const temTarefas = cp.tarefas_abertas > 0;
  const accentBorder = temHoje ? "border-l-red-500" : "border-l-amber-500";
  const accentBg = temHoje ? "bg-red-50" : "bg-amber-50";
  const accentIcon = temHoje ? "text-red-600 bg-red-100" : "text-amber-600 bg-amber-100";
  const accentTitle = temHoje ? "text-red-800" : "text-amber-800";

  // Formata hora curta HH:MM
  const hhmm = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };

  return (
    <div
      className={`mb-4 ${accentBg} border border-l-4 ${accentBorder} rounded-xl p-4`}
      data-testid="dashboard-chamadas-perdidas-widget"
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-start gap-2.5">
          <span className={`shrink-0 w-9 h-9 rounded-lg ${accentIcon} flex items-center justify-center mt-0.5`}>
            <PhoneMissed size={17} />
          </span>
          <div>
            <h3 className={`font-bold text-sm ${accentTitle}`} data-testid="dashboard-chamadas-perdidas-titulo">
              {temHoje
                ? `${cp.hoje} chamada${cp.hoje !== 1 ? "s" : ""} perdida${cp.hoje !== 1 ? "s" : ""} hoje`
                : "Reagendamentos pendentes"}
            </h3>
            <p className="text-[11px] text-slate-500">
              {temTarefas
                ? `${cp.tarefas_abertas} tarefa${cp.tarefas_abertas !== 1 ? "s" : ""} de reagendamento por fazer`
                : "Sem reagendamentos pendentes"}
              {cp.ultima_chamada && temHoje && (
                <span className="ml-1">- ultima as {hhmm(cp.ultima_chamada)}</span>
              )}
            </p>
          </div>
        </div>
        {/* Sparkline 7 dias */}
        {cp.por_dia && cp.por_dia.length > 0 && (
          <div className="shrink-0" title="Ultimos 7 dias">
            <Sparkline7 data={cp.por_dia} accentColor={temHoje ? "#dc2626" : "#d97706"} />
          </div>
        )}
      </div>

      <div className="flex items-center justify-end mb-2">
        <button
          onClick={() => nav("/app/tarefas")}
          className={`text-[11px] font-semibold ${temHoje ? "text-red-600 hover:text-red-800" : "text-amber-700 hover:text-amber-900"} hover:underline flex items-center gap-1`}
          data-testid="dashboard-chamadas-perdidas-ver-tarefas"
        >
          Ver tarefas de reagendamento <ArrowRight size={11} />
        </button>
      </div>

      {/* Top tarefas de reagendamento pendentes com botao de concluir */}
      {cp.tarefas && cp.tarefas.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="dashboard-chamadas-perdidas-lista">
          {cp.tarefas.map((t, i) => {
            const numero = t.meta?.numero || "";
            const resultado = t.meta?.resultado || "";
            const resultadoLabel = { nao_atendeu: "Nao atendeu", ocupado: "Ocupado", caixa_voz: "Caixa voz" }[resultado] || resultado;
            const prazoDate = t.prazo ? new Date(t.prazo) : null;
            const prazoStr = prazoDate ? prazoDate.toLocaleDateString("pt-PT", { day: "2-digit", month: "short" }) : "-";
            const overdue = prazoDate && prazoDate < new Date();
            return (
              <div
                key={t.id || i}
                className={`flex items-start gap-1.5 rounded-lg border px-2.5 py-2 text-[11px] ${overdue ? "bg-red-100 border-red-300" : "bg-white border-slate-200"}`}
                data-testid={`dashboard-chamadas-reagendamento-${i}`}
              >
                <button
                  onClick={() => nav(t.ref_id ? `/app/crm/contas/${t.ref_id}` : "/app/tarefas")}
                  className="flex-1 text-left"
                >
                  <div className="font-semibold text-navy truncate max-w-[160px]">{t.titulo}</div>
                  <div className="flex items-center gap-2 mt-0.5 text-slate-500">
                    {numero && <span className="font-mono">{numero}</span>}
                    {resultadoLabel && (
                      <span className={`text-[9px] font-bold uppercase px-1 rounded ${overdue ? "bg-red-200 text-red-800" : "bg-slate-100 text-slate-600"}`}>{resultadoLabel}</span>
                    )}
                    <span className="flex items-center gap-0.5"><CalendarClock size={9} />{prazoStr}</span>
                  </div>
                </button>
                {/* Botao de concluir tarefa */}
                <button
                  onClick={(e) => { e.stopPropagation(); onTaskComplete && onTaskComplete(t.id); }}
                  className="shrink-0 ml-1 w-6 h-6 rounded-full border border-emerald-300 bg-white hover:bg-emerald-100 flex items-center justify-center transition"
                  title="Marcar como concluida"
                  data-testid={`dashboard-chamadas-concluir-${i}`}
                >
                  <Check size={11} className="text-emerald-600" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


export default function Dashboard() {
  const [d, setD] = useState({});
  const [plataformaFiltro, setPlataformaFiltro] = useState(() => localStorage.getItem("sh_search_platform") || "");
  const [metas, setMetas] = useState(null);
  const [drStatus, setDrStatus] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const nav = useNavigate();
  const { can, user } = useAuth();
  const [mrrHistory, setMrrHistory] = useState(null);
  const [topLoc, setTopLoc] = useState(null);
  const [slaAnalytics, setSlaAnalytics] = useState(null);
  const [tarefasEmCurso, setTarefasEmCurso] = useState([]);
  const [chamadasPerdidas, setChamadasPerdidas] = useState(null); // {hoje, tarefas_abertas, tarefas, ultima_chamada}

  const loadTarefasEmCurso = () => {
    if (!user?.id) return;
    Promise.all([
      api.get("/team/tasks", { params: { responsavel_id: user.id, estado: "em_curso" } }).catch(() => ({ data: [] })),
      api.get("/team/tasks", { params: { responsavel_id: user.id, estado: "por_fazer" } }).catch(() => ({ data: [] })),
    ]).then(([emCursoRes, porFazerRes]) => {
      const now = new Date(); now.setHours(0, 0, 0, 0);
      const amanha = new Date(now); amanha.setDate(amanha.getDate() + 1);
      const emCurso = (emCursoRes.data || []).map((t) => ({ ...t, _badge: "em_curso" }));
      const hoje = (porFazerRes.data || []).filter((t) => {
        if (!t.prazo) return false;
        const d = new Date(t.prazo); d.setHours(0, 0, 0, 0);
        return d.getTime() >= now.getTime() && d.getTime() < amanha.getTime();
      }).map((t) => ({ ...t, _badge: "hoje" }));
      const merged = [...emCurso, ...hoje].sort((a, b) => (a.prazo || "9999").localeCompare(b.prazo || "9999"));
      setTarefasEmCurso(merged.slice(0, 8));
    });
  };

  const load = () => api.get("/dashboard", { params: plataformaFiltro ? { plataforma: plataformaFiltro } : {} })
    .then((r) => setD(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/dashboard/metas").then((r) => setMetas(r.data)).catch(() => {});
    if (can("subscricoes:read")) {
      api.get("/dashboard/mrr-history?months=12").then((r) => setMrrHistory(r.data?.snapshots || [])).catch(() => setMrrHistory([]));
      api.get("/dashboard/top-localidades", { params: { top: 10, ...(plataformaFiltro ? { plataforma: plataformaFiltro } : {}) } })
        .then((r) => setTopLoc(r.data)).catch(() => setTopLoc(null));
    }
    if (can("*")) api.get("/dr/status").then((r) => setDrStatus(r.data)).catch(() => {});
    if (can("crm:read")) api.get("/chat-ai/handoffs/analytics", { params: { dias: 7 } }).then((r) => setSlaAnalytics(r.data)).catch(() => {});
    if (can("crm:read")) api.get("/channels/letscall/missed-calls-summary").then((r) => setChamadasPerdidas(r.data)).catch(() => {});
    loadTarefasEmCurso();
  }, [plataformaFiltro]); // eslint-disable-line
  useAutoRefresh(load);

  // sincroniza com localStorage sempre que outra pagina/topbar mudar
  useEffect(() => {
    const onStorage = () => setPlataformaFiltro(localStorage.getItem("sh_search_platform") || "");
    window.addEventListener("storage", onStorage);
    const interval = setInterval(onStorage, 2000);
    return () => { window.removeEventListener("storage", onStorage); clearInterval(interval); };
  }, []);

  const alert = d.sync_alert;
  const platforms = d.por_plataforma || [];
  const leads = d.leads_por_canal || {};

  const retrySync = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post("/dashboard/sync-all");
      const ok = (data.results || []).filter((r) => r.ok);
      const fail = (data.results || []).filter((r) => !r.ok);
      if (ok.length && !fail.length) toast.success(`Sincronizacao concluida (${data.total_synced} subscricoes)`);
      else if (ok.length && fail.length) toast.warning(`Sincronizacao parcial: ${fail.map((f) => f.connector).join(", ")} falharam`);
      else toast.error("Sincronizacao falhou em todos os conectores. Verifique as credenciais.");
      load();
    } catch (e) {
      toast.error("Nao foi possivel sincronizar as plataformas.");
    } finally { setRetrying(false); }
  };

  const exportar = async (formato) => {
    try {
      const { data } = await api.get(`/dashboard/export?formato=${formato}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `relatorio-operacional.${formato === "pdf" ? "pdf" : "csv"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Nao foi possivel exportar o relatorio."); }
  };

  const concluirTarefa = async (taskId) => {
    try {
      await api.put(`/team/tasks/${taskId}`, { estado: "concluida" });
      toast.success("Tarefa concluida.");
      setTarefasEmCurso((prev) => prev.filter((t) => t.id !== taskId));
    } catch (e) { toast.error("Nao foi possivel concluir a tarefa."); }
  };

  // Cartoes "Acao necessaria" (RBAC)
  const acao = [];
  if (can("crm:write")) acao.push({ icon: ClipboardCheck, label: "Aprovacoes pendentes", value: d.aprovacoes_pendentes ?? 0, accent: (d.aprovacoes_pendentes > 0 ? "amber" : "navy"), to: "/app/aprovacoes", testid: "op-aprovacoes", urgent: d.aprovacoes_pendentes > 0 });
  if (can("crm:read")) acao.push({ icon: Clock, label: "Tarefas atrasadas", value: d.tarefas_atrasadas ?? 0, sub: `${d.tarefas_abertas ?? 0} abertas`, accent: (d.tarefas_atrasadas > 0 ? "red" : "navy"), to: "/app/tarefas", testid: "op-tarefas", urgent: d.tarefas_atrasadas > 0 });
  if (can("crm:read") && chamadasPerdidas != null) acao.push({ icon: PhoneMissed, label: "Chamadas perdidas hoje", value: chamadasPerdidas.hoje ?? 0, sub: chamadasPerdidas.tarefas_abertas > 0 ? `${chamadasPerdidas.tarefas_abertas} reagendamento(s) por fazer` : "Sem reagendamentos pendentes", accent: (chamadasPerdidas.hoje > 0 ? "red" : "navy"), to: "/app/tarefas", testid: "op-chamadas-perdidas", urgent: chamadasPerdidas.hoje > 0 });
  if (can("faturacao:read")) acao.push({ icon: AlertTriangle, label: "Faturas vencidas", value: d.faturas_vencidas ?? 0, sub: eur(d.valor_vencido || 0) + " por cobrar", accent: (d.faturas_vencidas > 0 ? "red" : "navy"), to: "/app/faturacao", testid: "op-vencidas", urgent: d.faturas_vencidas > 0 });
  if (can("tickets:read")) acao.push({ icon: LifeBuoy, label: "Tickets abertos", value: d.tickets_abertos ?? 0, accent: (d.tickets_abertos > 0 ? "amber" : "navy"), to: "/app/suporte", testid: "op-tickets" });
  if (can("cpq:read")) acao.push({ icon: FileClock, label: "Contratos a renovar (30d)", value: d.contratos_a_renovar ?? 0, accent: (d.contratos_a_renovar > 0 ? "amber" : "navy"), to: "/app/contratos", testid: "op-renovar" });

  // Metricas de suporte (30d) - RBAC tickets:read
  const sup = d.suporte_metricas || {};
  const verSuporte = can("tickets:read") && ((sup.tempo_medio_resposta_h != null) || (sup.tempo_medio_resolucao_h != null) || (sup.taxa_sla_pct != null));
  const fmtH = (v) => v == null ? "-" : (v < 1 ? `${Math.round(v * 60)} min` : v < 48 ? `${v} h` : `${Math.round(v / 24)} d`);

  // Comercial / CRM
  const comercial = [];
  if (can("crm:read")) {
    comercial.push({ icon: Users2, label: "Contas", value: d.contas_total ?? 0, to: "/app/crm/contas", testid: "op-contas", accent: "blue" });
    comercial.push({ icon: Target, label: "Oportunidades abertas", value: d.oportunidades_abertas ?? 0, to: "/app/crm/oportunidades", testid: "op-oportunidades", accent: "blue" });
    comercial.push({
      icon: MessageSquare, label: "Leads (canais)", value: d.leads_total ?? 0,
      sub: `${leads.formulario || 0} form. - ${leads.chat || 0} chat - ${leads.agendamento || 0} agend. - ${leads.email || 0} email - ${leads.chamada || 0} tel. - ${leads.manual || 0} manual`,
      to: "/app/canais", testid: "op-leads", accent: "green",
    });
  }
  if (can("cpq:read")) comercial.push({ icon: FileSignature, label: "Propostas por assinar", value: d.propostas_por_assinar ?? 0, to: "/app/propostas", testid: "op-propostas", accent: "blue" });

  const verFinanceiro = can("faturacao:read") || can("pagamentos:read");
  const verMetas = (can("cpq:read") || can("subscricoes:read") || verFinanceiro) && metas;

  const pct = (a, b) => (b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0);
  const barColor = (p) => (p >= 100 ? "bg-emerald-500" : p >= 60 ? "bg-blue" : "bg-amber-500");
  const MetaBar = ({ label, atual, meta, testid }) => {
    const p = pct(atual, meta);
    return (
      <div data-testid={testid}>
        <div className="flex items-center justify-between text-sm mb-1.5">
          <span className="text-navy font-medium">{label}</span>
          <span className="text-muted2">{eur(atual)}{meta > 0 ? <span className="text-slate-400"> / {eur(meta)}</span> : <span className="text-slate-400"> (sem meta)</span>}</span>
        </div>
        <div className="h-2.5 rounded-full bg-tint overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: `${p}%` }} transition={{ duration: 0.7 }} className={`h-full rounded-full ${barColor(p)}`} />
        </div>
        {meta > 0 && <div className="text-[11px] text-muted2 mt-1">{p}% da meta</div>}
      </div>
    );
  };

  return (
    <div>
      <PageHeader overline="Gestao operacional" title={`Ola, ${user?.nome?.split(" ")[0] || ""}`.trim() || "Dashboard"} />

      <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="dashboard-platform-bar">
        <span className="text-sm text-muted2">Vista:</span>
        <PlatformFilter value={plataformaFiltro} onChange={setPlataformaFiltro} testid="dashboard-platform-filter" />
        {plataformaFiltro && (
          <>
            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue/10 text-blue font-semibold">
              Filtrado por {plataformaFiltro}
            </span>
            <button onClick={() => setPlataformaFiltro("")} data-testid="dashboard-clear-platform"
              className="text-xs text-muted2 hover:text-navy underline">Ver todas</button>
          </>
        )}
      </div>

      <ConnectorCredentialsBanner />

      {alert?.failed && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} data-testid="sync-alert-banner"
          className="mb-6 flex items-start gap-3 border border-red-200 bg-red-50 rounded-lg p-4">
          <AlertTriangle className="text-red-600 mt-0.5 shrink-0" size={20} />
          <div className="flex-1">
            <div className="text-red-700 font-semibold text-sm">
              Falha na sincronizacao automatica de {(alert.failing || []).length} plataforma(s)
            </div>
            <ul className="text-red-600 text-sm mt-1 space-y-0.5">
              {(alert.failing || []).map((f) => (
                <li key={f.connector} data-testid={`sync-alert-item-${f.connector}`}>
                  <b>{f.platform_name}</b>: {f.error}
                  {f.last_run ? ` - ${new Date(f.last_run).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })}` : ""}
                </li>
              ))}
            </ul>
          </div>
          <button onClick={retrySync} disabled={retrying} data-testid="sync-alert-retry"
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 ${can("tenant_db:read") ? "" : "hidden"}`}>
            <RefreshCw size={14} className={retrying ? "animate-spin" : ""} /> Sincronizar todas
          </button>
        </motion.div>
      )}

      {d.mrr_alert && can("subscricoes:read") && (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          className={`mb-6 rounded-xl border p-4 flex items-start gap-3 ${
            d.mrr_alert.severity === "critico"
              ? "border-red-300 bg-red-50"
              : "border-amber-300 bg-amber-50"
          }`}
          data-testid="dashboard-mrr-alert">
          <AlertTriangle className={d.mrr_alert.severity === "critico" ? "text-red-600 mt-0.5 shrink-0" : "text-amber-600 mt-0.5 shrink-0"} size={20} />
          <div className="flex-1">
            <div className={`font-semibold text-sm ${d.mrr_alert.severity === "critico" ? "text-red-700" : "text-amber-800"}`}>
              Degradacao de MRR detectada
            </div>
            <div className={`text-sm mt-1 ${d.mrr_alert.severity === "critico" ? "text-red-600" : "text-amber-700"}`}>
              {d.mrr_alert.message} MRR anterior: {eur(d.mrr_alert.prev_mrr)} - Actual: {eur(d.mrr_alert.current_mrr)} ({d.mrr_alert.delta_pct}%).
            </div>
          </div>
          <button onClick={() => nav("/app/subscricoes")} data-testid="dashboard-mrr-alert-investigar"
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-white text-sm font-semibold transition-colors ${
              d.mrr_alert.severity === "critico" ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"
            }`}>
            Investigar
          </button>
        </motion.div>
      )}

      {can("cpq:read") && d.renewal_kpi && (
        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="dashboard-renewal-kpi">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-700 font-semibold">Contratos a expirar</div>
              <div className="text-xs text-muted2">Visao proactiva de renovacoes por janela</div>
            </div>
            <button onClick={() => nav("/app/contratos")} data-testid="dashboard-renewal-link"
              className="text-[11px] font-semibold text-emerald-700 hover:underline">Ver contratos &raquo;</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { k: "d30", label: "Proximos 30 dias", tone: "border-red-300 bg-red-50 text-red-700", val: "text-red-800" },
              { k: "d60", label: "30 - 60 dias", tone: "border-amber-300 bg-amber-50 text-amber-700", val: "text-amber-800" },
              { k: "d90", label: "60 - 90 dias", tone: "border-emerald-300 bg-emerald-50 text-emerald-700", val: "text-emerald-800" },
            ].map((c) => {
              const kpi = d.renewal_kpi[c.k] || { count: 0, valor: 0 };
              return (
                <button key={c.k} onClick={() => nav("/app/contratos")}
                  data-testid={`renewal-kpi-${c.k}`}
                  className={`text-left rounded-lg border ${c.tone} p-3 hover:shadow-sm transition`}>
                  <div className="text-[10px] font-bold uppercase tracking-wider">{c.label}</div>
                  <div className={`text-3xl font-bold mt-1 ${c.val}`}>{kpi.count}</div>
                  <div className="text-[11px] text-muted2">{eur(kpi.valor || 0)} em valor total</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {(d.triage_distribution || d.dpo_distribution) && (
        <div className="mb-6 rounded-xl border border-blue/20 bg-blue/5 p-4" data-testid="dashboard-triage-kpi">
          <div className="text-[10px] uppercase tracking-[0.15em] text-blue font-semibold mb-2">
            {d.triage_distribution ? "Servico de Triagem por tenant" : "Servico DPO por tenant"}
          </div>
          <div className="flex flex-wrap gap-3">
            {(() => {
              const dist = d.triage_distribution || d.dpo_distribution;
              const cards = [
                { key: "cogniseg", label: d.triage_distribution ? "DPO Cogniseg" : "DPO Cogniseg", tone: "bg-blue text-white", desc: "Cogniseg presta o servico" },
                { key: "client", label: d.triage_distribution ? "Triagem Cliente" : "DPO Cliente", tone: "bg-amber-500 text-white", desc: "Cliente gere internamente" },
                { key: "sem_info", label: "Sem info", tone: "bg-slate-400 text-white", desc: "Metadata em falta - editar em Ficha" },
              ];
              return cards.map((c) => (
                <div key={c.key} data-testid={`dashboard-triage-${c.key}`}
                  className="flex-1 min-w-[180px] bg-white border border-slate-200 rounded-lg p-3">
                  <div className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded ${c.tone}`}>
                    {c.label}
                  </div>
                  <div className="mt-1 text-2xl font-bold text-navy">{dist[c.key] || 0}</div>
                  <div className="text-[11px] text-muted2">{c.desc}</div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {!plataformaFiltro && d.totais_globais && (
        <div className="mb-6 rounded-xl border border-navy/10 bg-white p-4" data-testid="dashboard-totais-globais">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-navy font-semibold">Totais por plataforma</div>
              <div className="text-xs text-muted2">Distribuicao de tenants por prestador de servico (Cogniseg vs Cliente)</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.15em] text-blue font-semibold">Servico interno Cogniseg</div>
              <div className="text-2xl font-bold text-blue" data-testid="totais-interno-cogniseg">
                {d.totais_globais.servico_interno_total || 0}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { key: "cognietica", label: "CogniEtica", roleLabel: "Triagem", data: d.totais_globais.cognietica || {}, to: "/app/cognietica" },
              { key: "privaxia", label: "Privaxia", roleLabel: "DPO", data: d.totais_globais.privaxia || {}, to: "/app/privaxia" },
            ].map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => nav(p.to)}
                data-testid={`totais-card-${p.key}`}
                className="text-left border border-tint rounded-lg p-3 bg-slate-50/50 hover:border-blue hover:bg-white transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-navy font-bold text-sm">{p.label}</span>
                  <span className="text-2xl font-bold text-navy" data-testid={`totais-${p.key}-total`}>{p.data.total || 0}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue/10 text-blue font-semibold"
                    data-testid={`totais-${p.key}-cogniseg`}>
                    {p.data.cogniseg || 0} {p.roleLabel} Cogniseg
                  </span>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold"
                    data-testid={`totais-${p.key}-client`}>
                    {p.data.client || 0} Cliente
                  </span>
                  {p.data.sem_info > 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-semibold"
                      data-testid={`totais-${p.key}-sem-info`}>
                      {p.data.sem_info} Sem info
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {(acao.length > 0 || tarefasEmCurso.length > 0 || (chamadasPerdidas && (chamadasPerdidas.hoje > 0 || chamadasPerdidas.tarefas_abertas > 0))) && (
        <Section title="Acao necessaria">
          {/* Widget chamadas perdidas / reagendamentos */}
          {can("crm:read") && chamadasPerdidas && (chamadasPerdidas.hoje > 0 || chamadasPerdidas.tarefas_abertas > 0) && (
            <MissedCallsWidget
              cp={chamadasPerdidas}
              nav={nav}
              onTaskComplete={async (taskId) => {
                try {
                  await api.patch(`/channels/letscall/tasks/${taskId}`, { estado: "concluida" });
                  setChamadasPerdidas((prev) => {
                    if (!prev) return prev;
                    const newTarefas = prev.tarefas.filter((t) => t.id !== taskId);
                    return { ...prev, tarefas: newTarefas, tarefas_abertas: Math.max(0, (prev.tarefas_abertas || 1) - 1) };
                  });
                  toast.success("Tarefa concluida.");
                } catch (e) {
                  toast.error("Erro ao concluir tarefa.");
                }
              }}
            />
          )}
          {tarefasEmCurso.length > 0 && (
            <div className="mb-4 bg-white border border-tint border-l-4 border-l-blue rounded-xl p-5" data-testid="dashboard-tarefas-em-curso">              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="shrink-0 w-9 h-9 rounded-lg bg-tint text-blue flex items-center justify-center"><Clock size={18} /></span>
                  <div>
                    <h3 className="text-navy font-bold text-sm">Tarefas em curso e para hoje</h3>
                    <p className="text-[11px] text-muted2">O que precisa da sua atencao hoje - conclua direto a partir daqui.</p>
                  </div>
                </div>
                <button onClick={() => nav("/app/tarefas")} className="text-[11px] font-semibold text-blue hover:underline shrink-0" data-testid="dashboard-tarefas-ver-todas">
                  Ver todas <ArrowRight size={11} className="inline" />
                </button>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
                {tarefasEmCurso.map((t) => {
                  const horaPassada = t._badge === "hoje" && t.prazo && new Date(t.prazo).getTime() < Date.now();
                  return (
                    <div key={t.id} data-testid={`dashboard-tarefa-${t.id}`}
                      className={`shrink-0 w-64 border rounded-lg p-3 hover:shadow-md transition-shadow ${horaPassada ? "border-red-300 bg-red-50 ring-1 ring-red-200" : "border-tint bg-slate-50/60"}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${horaPassada ? "bg-red-100 text-red-700" : t._badge === "hoje" ? "bg-sky-100 text-sky-700" : "bg-blue/10 text-blue"}`}>
                          {horaPassada ? "Hoje - hora passada" : t._badge === "hoje" ? "Hoje" : "Em curso"}
                        </span>
                        {t.prioridade && <Badge value={t.prioridade} className="shrink-0" />}
                      </div>
                      <span className="text-sm font-semibold text-navy leading-snug line-clamp-2 block">{t.titulo}</span>
                      {t.prazo && <div className={`text-[11px] mt-1 ${horaPassada ? "text-red-600 font-semibold" : "text-muted2"}`}>Prazo: {dt(t.prazo)}</div>}
                      <button onClick={() => concluirTarefa(t.id)} data-testid={`dashboard-tarefa-concluir-${t.id}`}
                        className="mt-2 text-[11px] font-semibold text-emerald-700 hover:underline">
                        Marcar como concluida
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="op-acao">
            {acao.map((c, i) => <StatCard key={c.testid} {...c} delay={i * 0.04} />)}
          </div>
        </Section>
      )}

      {comercial.length > 0 && (
        <Section title="Comercial e CRM">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="op-comercial">
            {comercial.map((c, i) => <StatCard key={c.testid} {...c} delay={i * 0.04} />)}
          </div>
          {slaAnalytics && can("crm:read") && (
            <div className="mt-4 bg-white border border-tint rounded-xl p-4" data-testid="dashboard-sla-widget">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-blue font-bold">SLA de Contactos - 7 dias</div>
                  <div className="text-xs text-muted2">Chat, formularios e email inbound com fila unica</div>
                </div>
                <button onClick={() => nav("/app/handoffs")} className="text-[11px] text-blue underline hover:text-blue/80" data-testid="dashboard-sla-link">Ver fila completa <ArrowRight size={11} className="inline" /></button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="border border-tint rounded p-2">
                  <div className="text-[10px] uppercase text-muted2">Total pedidos</div>
                  <div className="text-xl font-bold text-navy">{slaAnalytics.total || 0}</div>
                  <div className="text-[10px] text-muted2">{slaAnalytics.respondidos || 0} respondidos</div>
                </div>
                <div className="border border-tint rounded p-2">
                  <div className="text-[10px] uppercase text-muted2">Tempo medio 1a resp.</div>
                  <div className="text-xl font-bold text-navy">{slaAnalytics.tempo_medio_resposta_min != null ? `${slaAnalytics.tempo_medio_resposta_min}` : "-"}</div>
                  <div className="text-[10px] text-muted2">minutos</div>
                </div>
                <div className="border border-tint rounded p-2">
                  <div className="text-[10px] uppercase text-muted2">Cumprimento SLA</div>
                  <div className={`text-xl font-bold ${(slaAnalytics.sla_cumprimento_pct ?? 100) >= 90 ? "text-emerald-600" : (slaAnalytics.sla_cumprimento_pct ?? 100) >= 70 ? "text-amber-600" : "text-red-600"}`}>{slaAnalytics.sla_cumprimento_pct != null ? `${slaAnalytics.sla_cumprimento_pct}%` : "-"}</div>
                  <div className="text-[10px] text-muted2">alvo &gt;=90%</div>
                </div>
                <div className="border border-tint rounded p-2">
                  <div className="text-[10px] uppercase text-muted2">SLA quebrado</div>
                  <div className={`text-xl font-bold ${slaAnalytics.breaches ? "text-red-600" : "text-emerald-600"}`}>{slaAnalytics.breaches || 0}</div>
                  <div className="text-[10px] text-muted2">alem do prazo</div>
                </div>
              </div>
              <div className="mt-3">
                <div className="text-[10px] uppercase text-muted2 mb-1">Volume diario</div>
                <div className="flex items-end gap-1 h-16 border-b border-tint">
                  {(slaAnalytics.serie_diaria || []).length === 0 && <div className="text-xs text-slate-400 italic self-center">Sem pedidos no periodo.</div>}
                  {(() => {
                    const rows2 = slaAnalytics.serie_diaria || [];
                    const maxV = Math.max(1, ...rows2.map((r) => r.total || 0));
                    return rows2.map((r) => (
                      <div key={r.dia} className="flex-1 flex flex-col items-center gap-0.5" title={`${r.dia}: ${r.total} (com ${r.comercial}, tec ${r.tecnico})`}>
                        <div className="w-full flex flex-col justify-end" style={{ height: "56px" }}>
                          <div className="w-full bg-blue-500" style={{ height: `${((r.comercial || 0) / maxV) * 56}px` }} />
                          <div className="w-full bg-amber-500" style={{ height: `${((r.tecnico || 0) / maxV) * 56}px` }} />
                        </div>
                        <div className="text-[8px] text-muted2 truncate w-full text-center">{r.dia.slice(5)}</div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          )}
        </Section>
      )}

      {verFinanceiro && (
        <Section title="Financeiro">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:col-span-1">
              <StatCard icon={Wallet} label="Valor em aberto" value={eur(d.valor_em_aberto || 0)} accent="amber" to="/app/faturacao" testid="op-em-aberto" />
              <StatCard icon={Euro} label="MRR" value={eur(d.mrr || 0)} accent="green" to="/app/subscricoes" testid="op-mrr" />
            </div>
            <div className="bg-white border border-tint rounded-xl p-5 lg:col-span-2" data-testid="op-top-dividas">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-navy text-sm">Clientes com maior divida em aberto</h3>
                <span className="text-xs text-muted2">Top 5</span>
              </div>
              {(d.top_dividas || []).length === 0 && <p className="text-sm text-muted2">Sem dividas em aberto.</p>}
              <div className="space-y-2">
                {(d.top_dividas || []).map((t, i) => (
                  <button key={i} onClick={() => t.account_id && nav(`/app/crm/contas/${t.account_id}`)}
                    className="w-full flex items-center justify-between text-sm py-2 px-3 rounded-lg hover:bg-tint transition-colors">
                    <span className="text-navy font-medium truncate max-w-[70%]">{t.organizacao || t.account_id || "-"}</span>
                    <span className="font-bold text-amber-600">{eur(t.valor)}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          {d.servico_interno && d.servico_interno.subscricoes > 0 && (
            <div className="mt-4 rounded-xl border border-blue/30 bg-gradient-to-r from-blue/5 to-white p-4"
              data-testid="servico-interno-kpi">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-blue font-bold">Servico Interno Cogniseg</div>
                  <div className="text-xs text-muted2">Receita recorrente dos tenants onde a Cogniseg presta triagem ou DPO</div>
                </div>
                <span className="text-[11px] text-muted2 font-semibold">
                  {d.servico_interno.subscricoes} subscricoes -
                  <span className="text-blue"> {d.servico_interno.cognietica_triagem} triagem</span> -
                  <span className="text-blue"> {d.servico_interno.privaxia_dpo} DPO</span>
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-white rounded-lg p-3 border border-blue/20" data-testid="interno-mrr">
                  <div className="text-[10px] uppercase tracking-wider text-blue font-semibold">MRR Interno</div>
                  <div className="text-2xl font-bold text-navy mt-1">{eur(d.servico_interno.mrr || 0)}</div>
                  <div className="text-[11px] text-muted2">receita mensal</div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue/20" data-testid="interno-arr">
                  <div className="text-[10px] uppercase tracking-wider text-blue font-semibold">ARR Interno</div>
                  <div className="text-2xl font-bold text-navy mt-1">{eur(d.servico_interno.arr || 0)}</div>
                  <div className="text-[11px] text-muted2">receita anualizada</div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue/20" data-testid="interno-share">
                  <div className="text-[10px] uppercase tracking-wider text-blue font-semibold">Peso no MRR total</div>
                  <div className="text-2xl font-bold text-navy mt-1">
                    {d.mrr > 0 ? Math.round((d.servico_interno.mrr / d.mrr) * 100) : 0}%
                  </div>
                  <div className="text-[11px] text-muted2">do MRR global</div>
                </div>
              </div>
            </div>
          )}
        </Section>
      )}

      {verMetas && (
        <Section title="Metas comerciais" right={<div className="flex items-center gap-3">
          <button onClick={() => exportar("csv")} data-testid="export-csv" className="text-xs text-blue font-semibold hover:underline">Exportar Excel</button>
          <button onClick={() => exportar("pdf")} data-testid="export-pdf" className="text-xs text-blue font-semibold hover:underline">Exportar PDF</button>
          <button onClick={() => nav("/app/definicoes")} className="text-xs text-muted2 font-semibold">Definir metas</button>
        </div>}>
          {metas.destaque && metas.destaque.vendas > 0 && (
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} data-testid="meta-destaque"
              className="mb-4 flex items-center gap-3 rounded-xl p-4 bg-gradient-to-r from-amber-50 to-white border border-amber-200">
              <span className="w-11 h-11 rounded-full bg-amber-400 text-white flex items-center justify-center shrink-0"><Trophy size={22} /></span>
              <div className="flex-1">
                <div className="text-amber-700 font-bold text-sm">Comercial do mes: {metas.destaque.nome || metas.destaque.email}</div>
                <div className="text-sm text-navy">{eur(metas.destaque.vendas)} em vendas{metas.destaque.meta > 0 ? ` (${Math.round((metas.destaque.vendas / metas.destaque.meta) * 100)}% da meta)` : ""}. {metas.destaque.atingiu ? "Meta atingida - parabens!" : "A caminho da meta, forca!"}</div>
              </div>
            </motion.div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white border border-tint rounded-xl p-5 space-y-5 lg:col-span-1">
              <MetaBar label="MRR" atual={metas.mrr_atual} meta={metas.mrr_meta} testid="meta-bar-mrr" />
              <MetaBar label="Vendas do mes" atual={metas.vendas_mes} meta={metas.vendas_meta} testid="meta-bar-vendas" />
            </div>
            <div className="bg-white border border-tint rounded-xl p-5 lg:col-span-2" data-testid="meta-comerciais">
              <h3 className="font-bold text-navy text-sm mb-3">Ranking de vendas do mes por comercial</h3>
              {(metas.por_comercial || []).length === 0 && <p className="text-sm text-muted2">Sem vendas nem metas definidas este mes.</p>}
              <div className="space-y-2">
                {(metas.por_comercial || []).map((c, i) => {
                  const p = pct(c.vendas, c.meta);
                  const medal = i === 0 ? "bg-amber-400 text-white" : i === 1 ? "bg-slate-300 text-navy" : i === 2 ? "bg-amber-700 text-white" : "bg-tint text-muted2";
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${medal}`}>{i + 1}</span>
                      <span className="text-sm text-navy w-44 truncate">{c.nome || c.email}</span>
                      <div className="flex-1 h-2 rounded-full bg-tint overflow-hidden"><div className={`h-full ${barColor(c.meta > 0 ? p : 100)}`} style={{ width: `${c.meta > 0 ? p : 100}%` }} /></div>
                      <span className="text-sm font-semibold text-navy w-28 text-right">{eur(c.vendas)}{c.meta > 0 && <span className="text-xs text-muted2"> / {eur(c.meta)}</span>}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Section>
      )}

      {platforms.length > 0 && can("subscricoes:read") && (
        <Section title="Subscricoes por plataforma" right={
          <span className="text-xs text-muted2">
            {d.subscricoes_externas || 0} via API - Total MRR {eur(platforms.reduce((s, p) => s + (p.mrr || 0), 0))} / ARR {eur(platforms.reduce((s, p) => s + (p.arr || p.mrr * 12 || 0), 0))}
          </span>
        }>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {platforms.map((p, i) => (
              <motion.button type="button" key={p.plataforma} onClick={() => nav(`/app/subscricoes?plataforma=${encodeURIComponent(p.plataforma)}`)}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} whileHover={{ y: -4 }}
                data-testid={`platform-mrr-${p.plataforma}`}
                className="text-left border border-tint rounded-xl p-5 bg-white hover:shadow-md hover:border-blue transition-all group">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-navy font-bold tracking-tight">{p.plataforma}</span>
                  <span className="text-xs bg-tint text-navy px-2 py-0.5 rounded-full font-semibold">{p.subscricoes} ativas</span>
                </div>
                <div className="text-navy text-2xl font-bold tracking-tight">{eur(p.mrr)}<span className="text-xs text-muted2 font-normal"> / mes</span></div>
                <div className="text-xs text-muted2 mt-1" data-testid={`platform-arr-${p.plataforma}`}>ARR {eur(p.arr || p.mrr * 12 || 0)}</div>
              </motion.button>
            ))}
          </div>
        </Section>
      )}

      {mrrHistory && mrrHistory.length > 0 && can("subscricoes:read") && (
        <Section title="Evolucao MRR (ultimos 12 meses)" right={<span className="text-xs text-muted2">{mrrHistory.length} snapshot(s)</span>}>
          <MrrHistoryChart snapshots={mrrHistory} />
        </Section>
      )}

      {topLoc && (topLoc.items || []).length > 0 && can("subscricoes:read") && (
        <Section title="Top Localidades por MRR" right={
          <span className="text-xs text-muted2" data-testid="top-loc-total">
            {topLoc.total_subscricoes} subscricao(oes) - {eur(topLoc.total_mrr)} MRR total
          </span>
        }>
          <TopLocalidadesChart data={topLoc} />
        </Section>
      )}

      {verSuporte && (
        <Section title="Suporte (ultimos 30 dias)" right={<span className="text-xs text-muted2">{sup.tickets_analisados || 0} ticket(s)</span>}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="suporte-metricas">
            <StatCard icon={Clock} label="Tempo medio de resposta" value={fmtH(sup.tempo_medio_resposta_h)} sub={sup.tickets_com_resposta ? `${sup.tickets_com_resposta} respondidos` : "sem respostas registadas"} accent={sup.tempo_medio_resposta_h == null ? "navy" : (sup.tempo_medio_resposta_h > 24 ? "amber" : "green")} to="/app/suporte" testid="sup-tempo-resposta" />
            <StatCard icon={LifeBuoy} label="Tempo medio de resolucao" value={fmtH(sup.tempo_medio_resolucao_h)} sub={sup.tickets_resolvidos ? `${sup.tickets_resolvidos} resolvidos` : "sem tickets resolvidos"} accent={sup.tempo_medio_resolucao_h == null ? "navy" : (sup.tempo_medio_resolucao_h > 72 ? "amber" : "blue")} to="/app/suporte" testid="sup-tempo-resolucao" />
            <StatCard icon={Activity} label="Cumprimento de SLA" value={sup.taxa_sla_pct == null ? "-" : `${sup.taxa_sla_pct} %`} sub={sup.taxa_sla_pct == null ? "sem dados" : (sup.taxa_sla_pct >= 90 ? "excelente" : sup.taxa_sla_pct >= 70 ? "aceitavel" : "abaixo do alvo")} accent={sup.taxa_sla_pct == null ? "navy" : (sup.taxa_sla_pct >= 90 ? "green" : sup.taxa_sla_pct >= 70 ? "amber" : "red")} to="/app/suporte" testid="sup-taxa-sla" />
          </div>
        </Section>
      )}

      <Section title="Metricas gerais">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="general-kpis">
          {GENERAL_CARDS.map((c, i) => (
            <motion.button type="button" key={c.key} onClick={() => nav(c.to)}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} whileHover={{ y: -4 }}
              data-testid={`kpi-${c.key}`}
              className="text-left border border-tint rounded-xl p-6 bg-white hover:shadow-md hover:border-blue transition-all group">
              <div className="flex items-center justify-between mb-4">
                <span className="text-muted2 text-xs uppercase tracking-[0.15em] font-semibold">{c.label}</span>
                <c.icon className="text-blue" size={20} />
              </div>
              <div className="text-navy text-3xl font-bold tracking-tight">{c.fmt ? c.fmt(d[c.key]) : (d[c.key] ?? 0)}</div>
            </motion.button>
          ))}
        </div>
      </Section>

      {drStatus && can("*") && <DrStatusSection status={drStatus} nav={nav} />}
    </div>
  );
}

function MrrHistoryChart({ snapshots }) {
  if (!snapshots || snapshots.length === 0) return null;

  // Cores por plataforma (auto-atribuidas)
  const palette = ["#3B82F6", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6", "#EF4444", "#14B8A6"];
  const platformSet = new Set();
  for (const s of snapshots) (s.by_platform || []).forEach((p) => platformSet.add(p.plataforma));
  const platforms = Array.from(platformSet);
  const colorFor = (name) => palette[platforms.indexOf(name) % palette.length];

  const width = 720, height = 240, pad = { l: 60, r: 20, t: 20, b: 40 };
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;
  const n = snapshots.length;
  const xStep = n > 1 ? innerW / (n - 1) : 0;

  const totalMax = Math.max(...snapshots.map((s) => s.total_mrr || 0), 1);
  const yScale = (v) => pad.t + innerH - (v / totalMax) * innerH;
  const xScale = (i) => pad.l + i * xStep;

  const totalPath = snapshots.map((s, i) => `${i === 0 ? "M" : "L"}${xScale(i)},${yScale(s.total_mrr || 0)}`).join(" ");
  const totalArea = `${totalPath} L${xScale(n - 1)},${pad.t + innerH} L${xScale(0)},${pad.t + innerH} Z`;

  const perPlatformPaths = platforms.map((plat) => {
    const pts = snapshots.map((s, i) => {
      const bp = (s.by_platform || []).find((x) => x.plataforma === plat);
      return { x: xScale(i), y: yScale(bp ? bp.mrr : 0) };
    });
    return { plat, d: pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" "), color: colorFor(plat) };
  });

  // Y-axis ticks (4 lines)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({ val: t * totalMax, y: pad.t + innerH - t * innerH }));

  const last = snapshots[snapshots.length - 1];
  const prev = snapshots.length > 1 ? snapshots[snapshots.length - 2] : null;
  const deltaPct = prev && prev.total_mrr > 0 ? ((last.total_mrr - prev.total_mrr) / prev.total_mrr) * 100 : 0;
  const deltaCls = deltaPct >= 0 ? "text-emerald-600" : deltaPct <= -10 ? "text-red-600" : "text-amber-600";

  return (
    <div data-testid="mrr-history-chart">
      <div className="flex items-baseline gap-4 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted2 font-semibold">MRR actual ({last.month})</div>
          <div className="text-2xl font-bold text-navy">{eur(last.total_mrr)}</div>
        </div>
        {prev && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted2 font-semibold">vs {prev.month}</div>
            <div className={`text-sm font-bold ${deltaCls}`}>
              {deltaPct >= 0 ? "+" : ""}{deltaPct.toFixed(1)}%
            </div>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} className="max-w-full">
          <defs>
            <linearGradient id="mrrTotalFill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#0B2545" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#0B2545" stopOpacity="0" />
            </linearGradient>
          </defs>
          {ticks.map((t, i) => (
            <g key={i}>
              <line x1={pad.l} x2={pad.l + innerW} y1={t.y} y2={t.y} stroke="#EEF2F7" strokeDasharray="3 3" />
              <text x={pad.l - 8} y={t.y + 4} fontSize="10" fill="#94A3B8" textAnchor="end">{eur(t.val).replace("€", "€").split(",")[0]}</text>
            </g>
          ))}
          <path d={totalArea} fill="url(#mrrTotalFill)" />
          <path d={totalPath} stroke="#0B2545" strokeWidth="2.5" fill="none" />
          {perPlatformPaths.map((p) => (
            <path key={p.plat} d={p.d} stroke={p.color} strokeWidth="1.5" strokeDasharray="4 3" fill="none" opacity="0.75" />
          ))}
          {snapshots.map((s, i) => (
            <g key={s.id}>
              <circle cx={xScale(i)} cy={yScale(s.total_mrr || 0)} r="3" fill="#0B2545">
                <title>{s.month}: {eur(s.total_mrr)}</title>
              </circle>
              <text x={xScale(i)} y={pad.t + innerH + 16} fontSize="10" fill="#64748B" textAnchor="middle">
                {s.month.substring(2).replace("-", "/")}
              </text>
            </g>
          ))}
        </svg>
      </div>
      <div className="flex flex-wrap gap-3 mt-2">
        <div className="flex items-center gap-1.5 text-xs">
          <span className="inline-block w-3 h-[3px] bg-navy" /> <span className="text-navy font-semibold">Total</span>
        </div>
        {platforms.map((p) => (
          <div key={p} className="flex items-center gap-1.5 text-xs">
            <span className="inline-block w-3 h-[3px] rounded" style={{ background: colorFor(p) }} />
            <span className="text-muted2">{p}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DrStatusSection({ status, nav }) {
  const s = status || {};
  const sched = s.schedule || {};
  const last = s.last_full || s.last_diff;
  const consecutive = sched.consecutive_failures || 0;
  const dot = (state) => ({ ok: "bg-emerald-500", warn: "bg-amber-500", error: "bg-red-500", off: "bg-slate-300" }[state]);
  const state = sched.last_error || consecutive > 0 ? "error" : sched.enabled ? "ok" : "off";
  const fmtBytes = (bytes) => {
    if (!bytes) return "0 B";
    const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0, v = bytes;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${u[i]}`;
  };
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "-";

  return (
    <div className="mb-8" data-testid="dr-dashboard-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-navy font-bold text-lg">Disaster Recovery</h2>
          <span data-testid="dr-dashboard-state" className={`inline-block w-2 h-2 rounded-full ${dot(state)}`} />
        </div>
        <button onClick={() => nav("/app/dr")} data-testid="dr-dashboard-open" className="text-blue text-sm hover:underline">Abrir DR</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-tint p-5" data-testid="dr-dashboard-last">
          <div className="text-muted2 text-xs uppercase tracking-[0.15em] font-semibold mb-2">Ultimo backup</div>
          {last ? (
            <>
              <div className="text-navy font-mono text-xs truncate">{last.id}</div>
              <div className="text-navy text-xl font-bold mt-1">{fmtBytes(last.size_bytes)}</div>
              <div className="text-slate-500 text-xs mt-0.5">{fmtDate(last.created_at)}</div>
            </>
          ) : <div className="text-slate-400 text-sm italic">Ainda sem backups</div>}
        </div>
        <div className="bg-white rounded-xl border border-tint p-5" data-testid="dr-dashboard-scheduler">
          <div className="text-muted2 text-xs uppercase tracking-[0.15em] font-semibold mb-2">Agendador</div>
          <div className={`text-lg font-semibold ${sched.enabled ? "text-emerald-700" : "text-slate-400"}`}>
            {sched.enabled ? "Ativo" : "Inativo"}
          </div>
          <div className="text-slate-500 text-xs mt-0.5">Ultimo full: {fmtDate(sched.last_full_at)}</div>
          <div className="text-slate-500 text-xs">Ultimo diff: {fmtDate(sched.last_diff_at)}</div>
          {consecutive > 0 && (
            <div className="text-red-600 text-xs mt-1 font-medium" data-testid="dr-dashboard-failures">{consecutive} falha(s) consecutiva(s)</div>
          )}
        </div>
        <div className="bg-white rounded-xl border border-tint p-5" data-testid="dr-dashboard-local">
          <div className="text-muted2 text-xs uppercase tracking-[0.15em] font-semibold mb-2">Armazenamento local</div>
          <div className="text-navy text-2xl font-bold">{fmtBytes(s.total_local_size_bytes || 0)}</div>
          <div className="text-slate-500 text-xs mt-0.5">{s.backup_count || 0} backups</div>
        </div>
        <div className="bg-white rounded-xl border border-tint p-5" data-testid="dr-dashboard-remotes">
          <div className="text-muted2 text-xs uppercase tracking-[0.15em] font-semibold mb-2">Destinos remotos</div>
          {(s.destinations || []).length === 0 && <div className="text-slate-400 text-sm italic">Sem destinos configurados</div>}
          <div className="space-y-1">
            {(s.destinations || []).slice(0, 3).map((d) => (
              <div key={d.id} className="flex items-center justify-between text-xs">
                <span className="text-navy truncate">{d.nome}</span>
                <span className="text-slate-500 font-mono">{fmtBytes(d.stats?.total_size || 0)}{d.stats?.errors > 0 && <span className="text-red-600"> !</span>}</span>
              </div>
            ))}
            {(s.destinations || []).length > 3 && <div className="text-slate-400 text-[10px]">+ {(s.destinations || []).length - 3} destinos</div>}
          </div>
        </div>
      </div>
      {sched.last_error && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800" data-testid="dr-dashboard-last-error">
          <b>Ultimo erro:</b> {sched.last_error}
        </div>
      )}
    </div>
  );
}


function TopLocalidadesChart({ data }) {
  const nav = useNavigate();
  const items = data?.items || [];
  const outras = data?.outras;
  const semLoc = data?.sem_localidade;
  // Construir slices: top items + Outras (se existir)
  const slices = [...items];
  if (outras && outras.mrr > 0) slices.push(outras);
  const totalPie = slices.reduce((s, x) => s + (x.mrr || 0), 0);
  if (totalPie <= 0) {
    return (
      <div className="rounded-xl border border-tint bg-white p-6 text-sm text-muted2 italic" data-testid="top-loc-empty">
        Sem dados de localidade nas subscricoes activas.
      </div>
    );
  }

  // Paleta consistente com o restante dashboard - Outras cinzento
  const palette = ["#1E3A5F", "#3B82F6", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6", "#14B8A6", "#EF4444", "#0EA5E9", "#84CC16"];
  const colorFor = (idx, label) => (label === "Outras" ? "#94A3B8" : palette[idx % palette.length]);

  // Geometria SVG donut
  const size = 260, cx = size / 2, cy = size / 2, R = 110, r = 62;
  let acc = 0;
  const arcs = slices.map((s, i) => {
    const frac = (s.mrr || 0) / totalPie;
    const a0 = acc * 2 * Math.PI - Math.PI / 2;
    acc += frac;
    const a1 = acc * 2 * Math.PI - Math.PI / 2;
    const large = frac > 0.5 ? 1 : 0;
    const p0 = { x: cx + R * Math.cos(a0), y: cy + R * Math.sin(a0) };
    const p1 = { x: cx + R * Math.cos(a1), y: cy + R * Math.sin(a1) };
    const p2 = { x: cx + r * Math.cos(a1), y: cy + r * Math.sin(a1) };
    const p3 = { x: cx + r * Math.cos(a0), y: cy + r * Math.sin(a0) };
    const d = `M${p0.x},${p0.y} A${R},${R} 0 ${large} 1 ${p1.x},${p1.y} L${p2.x},${p2.y} A${r},${r} 0 ${large} 0 ${p3.x},${p3.y} Z`;
    return { d, color: colorFor(i, s.localidade), item: s, pct: (frac * 100) };
  });

  return (
    <div className="rounded-xl border border-tint bg-white p-6" data-testid="top-localidades-widget">
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8 items-center">
        <div className="relative flex items-center justify-center">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="top-loc-pie">
            {arcs.map((a, i) => (
              <path key={i} d={a.d} fill={a.color} stroke="white" strokeWidth="2"
                data-testid={`top-loc-slice-${i}`}>
                <title>{`${a.item.localidade}: ${eur(a.item.mrr)} (${a.pct.toFixed(1)}%)`}</title>
              </path>
            ))}
            <text x={cx} y={cy - 6} textAnchor="middle" className="fill-navy" style={{ fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}>
              Total top
            </text>
            <text x={cx} y={cy + 14} textAnchor="middle" className="fill-navy" style={{ fontSize: 17, fontWeight: 700 }}>
              {eur(totalPie)}
            </text>
          </svg>
        </div>
        <div>
          <ul className="space-y-1.5" data-testid="top-loc-legend">
            {arcs.map((a, i) => (
              <li key={i}
                className="flex items-center justify-between gap-3 text-sm py-1.5 px-2 rounded hover:bg-tint/40 cursor-pointer"
                title={a.item.distrito ? `Distrito: ${a.item.distrito}` : ""}
                onClick={() => a.item.localidade !== "Outras" && nav(`/app/crm/contas?localidade=${encodeURIComponent(a.item.localidade)}`)}
                data-testid={`top-loc-item-${i}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: a.color }} />
                  <span className="text-navy font-medium truncate">
                    {a.item.localidade}
                    {a.item.distrito && a.item.localidade !== "Outras" && (
                      <span className="text-muted2 font-normal text-xs"> - {a.item.distrito}</span>
                    )}
                    {a.item.localidade === "Outras" && a.item.count_localidades && (
                      <span className="text-muted2 font-normal text-xs"> ({a.item.count_localidades} localidades)</span>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-muted2 tabular-nums">{a.item.subscricoes} sub</span>
                  <span className="text-navy font-semibold tabular-nums w-20 text-right">{eur(a.item.mrr)}</span>
                  <span className="text-xs text-muted2 tabular-nums w-12 text-right">{a.pct.toFixed(1)}%</span>
                </div>
              </li>
            ))}
          </ul>
          {semLoc && semLoc.subscricoes > 0 && (
            <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2"
              data-testid="top-loc-sem-info">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>
                <b>{semLoc.subscricoes}</b> subscricao(oes) sem localidade preenchida na conta CRM
                {semLoc.mrr > 0 && <> ({eur(semLoc.mrr)} MRR nao mapeado)</>}. Complete o campo <i>Localidade</i> na Ficha da conta.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
